import { CustomCalendarType } from "@ac-expo/api-types";
import { isPlanningGroup, PlanningEvent, PlanningEventGroup } from "../utils";
import dayjs from "dayjs";
import GroupedEventsCard from "./GroupedEventsCard";
import SingleEventCard from "./SingleEventCard";

type PlanningEventCardProps = {
  event: PlanningEvent | PlanningEventGroup;
  withRegister?: boolean;
  openDetails?: (events: PlanningEvent[]) => void;
  customCalendar?: CustomCalendarType;
  isMobile?: boolean;
};

export function PlanningEventCard({
  event,
  openDetails,
  withRegister,
  customCalendar,
  isMobile = false,
}: PlanningEventCardProps) {
  const hours = getPlanningEventHours(event);

  if (isPlanningGroup(event)) {
    return (
      <GroupedEventsCard
        group={event}
        openGroupDetails={openDetails}
        hours={hours}
        customCalendar={customCalendar}
      />
    );
  }

  const isSlim = dayjs(event.end).diff(event.start, "minutes") <= 30;
  const isCompact = customCalendar?.model === "verticalByPlace" || customCalendar?.model === "verticalDayHour";

  const date = getEventDate(event, customCalendar?.model, isMobile);

  return (
    <SingleEventCard
      event={event}
      hours={hours}
      isSlim={isSlim}
      date={date}
      withRegistration={withRegister}
      openDetails={openDetails ? () => openDetails([event]) : undefined}
      allDayBlockColor={customCalendar?.fullDayColor ?? "orange"}
      isMobile={isMobile}
      isCompact={isCompact}
    />
  );
}

function getEventDate(
  event: PlanningEvent,
  model: CustomCalendarType["model"] | undefined,
  isMobile: boolean
): string | undefined {
  switch (model) {
    case "verticalByPlace":
      return getPlanningEventDate(event);
    case "verticalWeekly":
      return isMobile ? getPlanningEventDay(event) : undefined;
    default:
      return undefined;
  }
}

export function getPlanningEventHours(
  event: PlanningEvent | PlanningEventGroup
): string {
  const start = dayjs(event.start);
  const end = dayjs(event.end);
  return `${start.format("HH:mm")} - ${end.format("HH:mm")}`;
}

export function getPlanningEventDate(
  event: PlanningEvent | PlanningEventGroup
): string {
  return dayjs(event.start).format("dddd DD MMMM");
}

export function getPlanningEventDay(
  event: PlanningEvent | PlanningEventGroup
): string {
  return dayjs(event.start).format("dddd");
}

export default PlanningEventCard;
